/**
 * The login screen's line-art shipwreck, as pure data and pure maths.
 *
 * Everything here is a function of choreography time (see `intro_pace.ts`), with no canvas and no
 * pixi, so the shape of the scene can be asserted in a unit test: the ship arrives where it should,
 * the bow rides up when it strikes, the sail tears and stays torn.
 *
 * Coordinates are the GUI's logical screen (800 x 460, see intro_theme). The scene is AUTHORED with
 * the ship on the left, sailing right; `SHIP_ON_RIGHT` mirrors the whole drawing about the screen's
 * centre line at paint time, so the geometry never has to be written twice.
 */

export type Pt = [number, number]

export interface Shape {
    pts: Pt[]
    closed: boolean
}

/** The ship runs aground on the right of the card, sailing west into the land on the left. */
export const SHIP_ON_RIGHT = true
export const MIRROR_AXIS = 400

export const mirrorX = (x: number): number => (SHIP_ON_RIGHT ? 2 * MIRROR_AXIS - x : x)

// ---- timing -----------------------------------------------------------------------------------

/**
 * When each piece of the scene happens, in choreography ms after the loading screen hands over.
 * The wordmark's flight runs on the same clock (logo_choreography FLIGHT_*), so these are written
 * against it: night falls while the letters are in the air, the ship is drawn once the card is up.
 */
export const SCENE = {
    backdrop: [250, 1350] as const,
    horizon: [700, 1900] as const,
    water: [900, 2700] as const,
    stars: 1300,
    moon: [1250, 2250] as const,
    cloud: [1750, 2950] as const,
    land: [1600, 3300] as const,
    rocks: [2100, 3000] as const,
    shipInk: [2000, 3600] as const,
    sail: [2600, 5400] as const,
    lantern: 3600,
    impact: 5400,
}

/** After this the static layer (sky, horizon, moon, shore) never changes again. */
export const STATIC_DONE = 3700

/** Nothing is drawing in any more and the strike has played out: only the idle loop is left. */
export const REST = SCENE.impact + 1400

// ---- maths ------------------------------------------------------------------------------------

export const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v)
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t
export const prog = (t: number, a: number, b: number): number => clamp01((t - a) / (b - a))
export const smooth = (v: number): number => {
    const t = clamp01(v)
    return t * t * (3 - 2 * t)
}
export const easeOutCubic = (v: number): number => 1 - Math.pow(1 - clamp01(v), 3)
export const easeInOutCubic = (v: number): number => {
    const t = clamp01(v)
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}
export const easeInOutSine = (v: number): number => -(Math.cos(Math.PI * clamp01(v)) - 1) / 2

/** A small seeded generator, so the water, the stars and the spray are the same on every visit. */
export const rng = (seed: number) => (): number => {
    seed |= 0
    seed = (seed + 0x6d2b79f5) | 0
    let r = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296
}

/**
 * A tiny path language -- absolute M, L, Q, C and Z -- flattened to a polyline, so every stroke can
 * be drawn partway (the ink-in) and bent point by point (the masts whipping on impact).
 */
export const flat = (d: string, n: number = 10): Shape => {
    const tokens = d.match(/[MLQCZ]|-?\d*\.?\d+/g) || []
    const pts: Pt[] = []
    let i = 0
    let cmd = ""
    let cx = 0
    let cy = 0
    let closed = false
    const num = () => parseFloat(tokens[i++])
    while (i < tokens.length) {
        if (/[MLQCZ]/.test(tokens[i])) {
            cmd = tokens[i++]
        }
        if (cmd === "M" || cmd === "L") {
            cx = num()
            cy = num()
            pts.push([cx, cy])
        } else if (cmd === "Q") {
            const x1 = num(), y1 = num(), x = num(), y = num()
            for (let s = 1; s <= n; s++) {
                const u = s / n, v = 1 - u
                pts.push([v * v * cx + 2 * v * u * x1 + u * u * x, v * v * cy + 2 * v * u * y1 + u * u * y])
            }
            cx = x
            cy = y
        } else if (cmd === "C") {
            const x1 = num(), y1 = num(), x2 = num(), y2 = num(), x = num(), y = num()
            for (let s = 1; s <= n; s++) {
                const u = s / n, v = 1 - u
                pts.push([
                    v * v * v * cx + 3 * v * v * u * x1 + 3 * v * u * u * x2 + u * u * u * x,
                    v * v * v * cy + 3 * v * v * u * y1 + 3 * v * u * u * y2 + u * u * u * y,
                ])
            }
            cx = x
            cy = y
        } else if (cmd === "Z") {
            closed = true
        } else {
            // A number with no command in front of it: malformed path. Stop rather than spin.
            break
        }
    }
    return { pts, closed }
}

// ---- the ship ---------------------------------------------------------------------------------

/**
 * Ship-local units: bow to +x, waterline at y = 0, up is negative. About 170 long; the mainmast
 * truck is at -198. `ink` is the slice of the ship's drawing-in window this stroke draws in.
 */
export interface ShipPart {
    shape?: Shape
    /** Rebuilt every frame (the torn sail, the pennant). */
    dyn?: (st: ShipState, t: number) => Pt[]
    closed?: boolean
    ink: [number, number]
    /** Stroke width and alpha. */
    w?: number
    a?: number
    /** Knocked out with the sky before stroking, so it hides what is behind it. */
    fill?: boolean
    /** A faint wash of ink over the knock-out: sails read as canvas, not holes. */
    tone?: number
    /** Above the deck: bends with the masts. */
    rig?: boolean
    /** A local transform applied before everything else (the fore topsail yard swinging loose). */
    pre?: (x: number, y: number, st: ShipState) => Pt
    alphaFn?: (st: ShipState) => number
}

export interface ShipState {
    x: number
    y: number
    s: number
    /** The water's surface under the ship. Everything of the ship below it is hidden. */
    wl: number
    cos: number
    sin: number
    bend: number
    yard: number
    tear: number
    swing: number
    ink: number
    alpha: number
}

/** Far out on the water, small, where the ship is first drawn; and aground beside the card. */
export const SHIP_FAR = { x: 36, y: 350, s: 0.5 }
export const SHIP_AGROUND = { x: 96, y: 390, s: 0.88 }
/** The hull pivots about its stern foot as the bow rides up the rock. */
export const PIVOT: Pt = [-50, 14]
/** Where the bow meets the rock, in screen coordinates (authored side). */
export const CONTACT: Pt = [SHIP_AGROUND.x + 72 * SHIP_AGROUND.s, SHIP_AGROUND.y]

/** The swell's beat (choreography ms per radian): the ship's pitch and heave follow it. */
export const SWELL_PERIOD = 430
const STORM_PITCH = 0.075

/** A breaker crashes over the rock and the stranded hull this often once she is aground. */
export const BREAKER_MS = 2600
const BREAKERS_FROM = SCENE.impact + 1500

/** When the last breaker hit, at or before `t` (-Infinity before the first). */
export const lastBreaker = (t: number): number => {
    if (t < BREAKERS_FROM) return -Infinity
    const n = Math.floor((t - BREAKERS_FROM) / BREAKER_MS)
    return BREAKERS_FROM + n * BREAKER_MS + rng(300 + n)() * 500
}

export const shipStateAt = (t: number): ShipState => {
    const u = prog(t, SCENE.sail[0], SCENE.sail[1])
    // Slightly accelerating: perspective makes a ship coming at you seem to gather speed.
    const p = Math.pow(u, 1.35)
    const s = lerp(SHIP_FAR.s, SHIP_AGROUND.s, p)
    let x = lerp(SHIP_FAR.x, SHIP_AGROUND.x, p)
    let y = lerp(SHIP_FAR.y, SHIP_AGROUND.y, p)
    const wl = y
    const dt = t - SCENE.impact
    const afloat = dt < 0 ? 1 : 1 - smooth(dt / 300)

    // Running before a gale: she pitches through the swell, bow plunging as she slides off each crest.
    let rot = (STORM_PITCH * Math.sin(t / SWELL_PERIOD) + 0.016 * Math.sin(t / 190 + 1)) * afloat
    y += 4 * s * Math.sin(t / SWELL_PERIOD + 1.3) * afloat
    let bend = (0.03 * Math.sin(t / SWELL_PERIOD + 0.6) + 0.008 * Math.sin(t / 170)) * afloat
    let yard = 0
    let tear = 0
    let swing = 0.26 * Math.sin(t / SWELL_PERIOD + 0.5) * afloat

    if (dt >= 0) {
        // Bow up: a negative angle lifts the +x end. A quick rise with one small overshoot.
        rot += -0.1 * (1 - Math.exp(-dt / 120) * Math.cos(dt / 95)) + 0.006 * Math.sin(dt / 1500) * smooth((dt - 600) / 900)
        x += s * 4 * (1 - Math.exp(-dt / 70))
        y -= s * 3 * (1 - Math.exp(-dt / 100))
        // The masts keep going when the hull stops: tops lurch forward, then ring down.
        bend += 0.075 * Math.exp(-dt / 420) * Math.sin(dt / 85) + 0.008 * Math.sin(dt / 1700) * smooth((dt - 800) / 1000)
        yard = 0.28 * (1 - Math.exp(-dt / 200) * Math.cos(dt / 120)) + 0.03 * Math.sin(dt / 900) * smooth((dt - 700) / 800)
        tear = smooth((dt - 60) / 450)
        swing += 0.55 * Math.exp(-dt / 1600) * Math.sin(dt / 230) + 0.12 * Math.sin(t / 700)
        // Aground, each breaker that hits her makes the hull shudder and the masts whip.
        const hit = t - lastBreaker(t)
        if (hit >= 0 && hit < 700) {
            const e = Math.exp(-hit / 160)
            rot += 0.012 * e * Math.sin(hit / 45)
            bend += 0.03 * e * Math.sin(hit / 70)
        }
    }

    const ink = prog(t, SCENE.shipInk[0], SCENE.shipInk[1])
    const near = smooth((s - SHIP_FAR.s) / (SHIP_AGROUND.s - SHIP_FAR.s))
    const alpha = (0.6 + 0.4 * near) * (0.35 + 0.65 * smooth(ink * 1.4))
    return { x, y, s, wl, cos: Math.cos(rot), sin: Math.sin(rot), bend, yard, tear, swing, ink, alpha }
}

/** Ship-local to screen (authored side). */
export const shipPoint = (st: ShipState, x: number, y: number, rig?: boolean): Pt => {
    if (rig) {
        const h = -31 - y
        if (h > 0) {
            x += (st.bend * h * h) / 170
        }
    }
    const dx = x - PIVOT[0]
    const dy = y - PIVOT[1]
    const xr = PIVOT[0] + dx * st.cos - dy * st.sin
    const yr = PIVOT[1] + dx * st.sin + dy * st.cos
    return [st.x + xr * st.s, st.y + yr * st.s]
}

/** The fore course. At the strike its lee clew tears free and flaps from then on. */
export const foreCoursePoints = (st: ShipState, t: number): Pt[] => {
    const tp = st.tear
    const loose: Pt = [
        80 + 5 * Math.sin(t / 170) + 2.5 * Math.sin(t / 63),
        -74 + 6 * Math.sin(t / 210 + 1) + 2 * Math.sin(t / 71),
    ]
    const br: Pt = [lerp(95, loose[0], tp), lerp(-58, loose[1], tp)]
    const pts: Pt[] = [
        [26, -105],
        [94, -105],
    ]
    const c1: Pt = [100 - 8 * tp, -90]
    const c2: Pt = [100 - 14 * tp, -72 + 4 * tp]
    for (let s = 1; s <= 10; s++) {
        const u = s / 10, v = 1 - u
        pts.push([
            v * v * v * 94 + 3 * v * v * u * c1[0] + 3 * v * u * u * c2[0] + u * u * u * br[0],
            v * v * v * -105 + 3 * v * v * u * c1[1] + 3 * v * u * u * c2[1] + u * u * u * br[1],
        ])
    }
    // The foot, ragged once it has torn.
    const ctl: Pt = [60, -52 + 6 * tp]
    for (let s = 1; s <= 9; s++) {
        const u = s / 10, v = 1 - u
        const x = v * v * br[0] + 2 * v * u * ctl[0] + u * u * 26
        let y = v * v * br[1] + 2 * v * u * ctl[1] + u * u * -58
        y += (s % 2 ? 1 : -1) * 3.2 * tp * (1 - u * 0.6)
        pts.push([x, y])
    }
    pts.push([26, -58])
    for (let s = 1; s <= 8; s++) {
        const u = s / 8, v = 1 - u
        pts.push([
            v * v * v * 26 + 3 * v * v * u * 21 + 3 * v * u * u * 21 + u * u * u * 26,
            v * v * v * -58 + 3 * v * v * u * -72 + 3 * v * u * u * -90 + u * u * u * -105,
        ])
    }
    return pts
}

/** The pennant at the main truck, streaming toward the bow. */
export const pennantPoints = (_st: ShipState, t: number): Pt[] => {
    const up: Pt[] = []
    const lo: Pt[] = []
    const n = 9
    const len = 30
    for (let i = 0; i <= n; i++) {
        const u = i / n
        const x = -6 + u * len
        const wave = Math.sin(t / 70 - u * 6) * 3 * u + u * 2
        const hw = 2.4 * (1 - u) + 0.2
        up.push([x, -198 + wave - hw])
        lo.push([x, -198 + wave + hw])
    }
    return up.concat(lo.reverse())
}

/** The fore topsail yard swings loose at the strike, and the head of the sail swings with it. */
export const yardSwing = (weight: (y: number) => number) => (x: number, y: number, st: ShipState): Pt => {
    const w = weight(y)
    if (!w || !st.yard) {
        return [x, y]
    }
    const a = st.yard * w
    const cs = Math.cos(a), sn = Math.sin(a)
    const dx = x - 60, dy = y + 150
    return [60 + dx * cs - dy * sn, -150 + dx * sn + dy * cs]
}
export const topsailWeight = (y: number) => clamp01((-111 - y) / 38)

const buildShip = (): ShipPart[] => {
    const parts: ShipPart[] = []
    const add = (d: string, o: Omit<ShipPart, "shape">) => parts.push({ shape: flat(d), ...o })
    const rigLine = { w: 0.7, a: 0.5, rig: true }

    add("M 78 -50 L 130 -78", { ink: [0.22, 0.36], w: 1.2 }) // bowsprit

    // Standing rigging, behind everything else.
    add("M 60 -168 L 128 -77", { ...rigLine, ink: [0.6, 0.84], a: 0.55 })
    add("M -6 -194 L 56 -54", { ...rigLine, ink: [0.62, 0.86] })
    add("M -6 -196 L 60 -168", { ...rigLine, ink: [0.64, 0.86] })
    add("M -62 -140 L -6 -112", { ...rigLine, ink: [0.66, 0.88] })
    add("M -6 -190 L -40 -58", { ...rigLine, ink: [0.66, 0.9], a: 0.42 })
    const fan = (top: Pt, feet: Pt[], a: number, b: number) =>
        feet.forEach(f => add(`M ${top[0]} ${top[1]} L ${f[0]} ${f[1]}`, { ...rigLine, ink: [a, b], w: 0.6, a: 0.45 }))
    fan([-6, -150], [[-26, -33], [-19, -32], [-12, -31], [1, -31], [8, -31], [15, -32]], 0.6, 0.82)
    fan([60, -131], [[47, -51], [53, -51], [67, -51], [73, -51]], 0.62, 0.84)
    fan([-62, -124], [[-73, -61], [-68, -61], [-56, -60], [-51, -60]], 0.64, 0.86)
    const ratlines = (top: Pt, foot: number, l1: number, l2: number, r1: number, r2: number, ys: number[]) =>
        ys.forEach(y => {
            const u = (y - top[1]) / (foot - top[1])
            const xs = [l1, l2, r1, r2].map(x => top[0] + (x - top[0]) * u)
            const o = { ...rigLine, ink: [0.78, 0.96] as [number, number], w: 0.5, a: 0.35 }
            if (xs[1] - xs[0] > 2) add(`M ${xs[0]} ${y} L ${xs[1]} ${y}`, o)
            if (xs[3] - xs[2] > 2) add(`M ${xs[2]} ${y} L ${xs[3]} ${y}`, o)
        })
    ratlines([-6, -150], -32, -26, -12, 1, 15, [-46, -60, -74, -88, -102, -116])
    ratlines([60, -131], -51, 47, 53, 67, 73, [-62, -76, -90])

    // Masts.
    add("M -62 -61 L -62 -142", { ink: [0.2, 0.4], w: 1.3, rig: true })
    add("M -6 -31 L -6 -198", { ink: [0.18, 0.42], w: 1.4, rig: true })
    add("M 60 -51 L 60 -170", { ink: [0.2, 0.42], w: 1.3, rig: true })

    // The hull and its planking.
    add("M -84 -62 L -44 -60 L -44 -36 Q 0 -28 48 -34 L 48 -50 L 88 -52 C 92 -24 84 0 64 14 C 40 24 -30 26 -60 18 L -72 8 Z", {
        ink: [0, 0.32],
        w: 1.45,
        fill: true,
        tone: 0.025,
    })
    add("M -44 -33 Q 0 -25 48 -31", { ink: [0.28, 0.46], w: 0.9, a: 0.85 })
    add("M -83.5 -54 L -44 -53", { ink: [0.3, 0.46], w: 0.9, a: 0.85 })
    add("M 48 -45 L 89 -46", { ink: [0.3, 0.46], w: 0.9, a: 0.85 })
    add("M -78 -26 Q 0 -6 88 -24", { ink: [0.3, 0.5], w: 1 })
    add("M -75 -8 Q 0 10 83 -7", { ink: [0.34, 0.54], w: 0.9, a: 0.8 })
    // Balusters along the castle rails.
    const aftTop = (x: number) => -62 + (x + 84) / 20
    const aftBalusters = [-78, -71, -64, -57, -50]
    aftBalusters.forEach(x => add(`M ${x} ${aftTop(x) + 0.6} L ${x} -54`, { ink: [0.46, 0.62], w: 0.65, a: 0.7 }))
    const foreTop = (x: number) => -50 - (x - 48) / 20
    const foreBalusters = [55, 62, 69, 76, 83]
    foreBalusters.forEach(x => add(`M ${x} ${foreTop(x) + 0.6} L ${x} -45.5`, { ink: [0.46, 0.62], w: 0.65, a: 0.7 }))
    // Gun ports along the waist, and a few in the castles.
    const port = (x: number, y: number, s: number = 5.5) =>
        add(`M ${x} ${y} L ${x + s} ${y} L ${x + s} ${y + s} L ${x} ${y + s} Z`, { ink: [0.44, 0.64], w: 0.8, a: 0.8 })
    const waistPorts = [-32, -17, -2, 13, 28]
    waistPorts.forEach(x => port(x, -27.5))
    port(-68, -48, 5)
    port(-57, -48, 5)
    port(60, -43, 5)
    port(71, -43, 5)
    port(-80, -50, 4)

    // Mizzen lateen, behind the main.
    add("M -30 -78 L -96 -160 C -92 -120 -86 -90 -82 -66 Q -56 -66 -30 -78 Z", { ink: [0.46, 0.74], w: 1, fill: true, tone: 0.05, rig: true })
    add("M -26 -73 L -100 -165", { ink: [0.42, 0.58], w: 1.1, rig: true })
    // Main course and topsail.
    add("M -52 -121 L 40 -121 C 48 -103 48 -84 42 -68 Q -6 -60 -54 -68 C -60 -84 -60 -103 -52 -121 Z", {
        ink: [0.48, 0.78],
        w: 1.1,
        fill: true,
        tone: 0.06,
        rig: true,
    })
    add("M -26 -120 C -32 -100 -32 -82 -28 -64", { ink: [0.62, 0.84], w: 0.7, a: 0.55, rig: true })
    add("M 14 -120 C 18 -100 18 -82 16 -64", { ink: [0.64, 0.86], w: 0.7, a: 0.55, rig: true })
    add("M -33 -173 L 21 -173 C 27 -160 27 -140 23 -128 Q -6 -123 -35 -128 C -39 -140 -39 -160 -33 -173 Z", {
        ink: [0.5, 0.78],
        w: 1.05,
        fill: true,
        tone: 0.06,
        rig: true,
    })
    add("M -14 -172 C -17 -158 -17 -142 -15 -126", { ink: [0.66, 0.86], w: 0.65, a: 0.5, rig: true })
    add("M 4 -172 C 6 -158 6 -142 5 -126", { ink: [0.66, 0.86], w: 0.65, a: 0.5, rig: true })

    // Fore course: torn at the strike.
    parts.push({ dyn: foreCoursePoints, closed: true, ink: [0.5, 0.78], w: 1.1, fill: true, tone: 0.06, rig: true })
    add("M 46 -104 C 41 -88 41 -72 44 -57", { ink: [0.64, 0.86], w: 0.7, a: 0.55, rig: true })
    add("M 74 -104 C 78 -88 78 -72 76 -57", { ink: [0.66, 0.88], w: 0.7, a: 0.55, rig: true, alphaFn: st => 1 - st.tear })

    // Fore topsail: its yard swings askew at the strike.
    const topsail = yardSwing(topsailWeight)
    add("M 38 -149 L 82 -149 C 87 -138 87 -122 84 -111 Q 60 -107 36 -111 C 33 -122 33 -138 38 -149 Z", {
        ink: [0.52, 0.8],
        w: 1.05,
        fill: true,
        tone: 0.06,
        rig: true,
        pre: topsail,
    })
    add("M 52 -148 C 50 -136 50 -124 51 -110", { ink: [0.68, 0.88], w: 0.65, a: 0.5, rig: true, pre: topsail })
    add("M 68 -148 C 70 -136 70 -124 69 -110", { ink: [0.68, 0.88], w: 0.65, a: 0.5, rig: true, pre: topsail })

    // Yards and tops.
    add("M -56 -122 L 44 -122", { ink: [0.4, 0.56], w: 1.3, rig: true })
    add("M -36 -174 L 24 -174", { ink: [0.42, 0.58], w: 1.2, rig: true })
    add("M 22 -106 L 98 -106", { ink: [0.42, 0.58], w: 1.3, rig: true })
    add("M 36 -150 L 84 -150", { ink: [0.44, 0.6], w: 1.2, rig: true, pre: yardSwing(() => 1) })
    add("M -15 -152 L 3 -152 L 1 -146 L -13 -146 Z", { ink: [0.55, 0.66], w: 1, fill: true, rig: true })
    add("M 52 -132 L 68 -132 L 66 -127 L 54 -127 Z", { ink: [0.56, 0.67], w: 1, fill: true, rig: true })

    parts.push({ dyn: pennantPoints, closed: true, ink: [0.85, 1], w: 0.8, fill: true, tone: 0.12, rig: true })
    return parts
}

export const SHIP_PARTS: ShipPart[] = buildShip()

/** The stern lantern: the only warm light in the scene. The last thing drawn, then it is lit. */
export interface Lantern {
    bracket: Pt[]
    body: Pt[]
    cap: Pt[]
    glow: Pt
    /** 0 before the lantern is drawn, 1 once it is. */
    drawn: number
    /** 0 until it lights, then 1; `flare` is the flash as it catches. */
    lit: number
    flare: number
    flicker: number
}

export const lanternAt = (st: ShipState, t: number): Lantern => {
    const hook: Pt = [-93, -64]
    const cs = Math.cos(st.swing), sn = Math.sin(st.swing)
    const hang = (x: number, y: number): Pt => [hook[0] + x * cs - y * sn, hook[1] + x * sn + y * cs]
    const at = (p: Pt) => shipPoint(st, p[0], p[1])
    const lit = prog(t, SCENE.lantern, SCENE.lantern + 350)
    return {
        bracket: [at([-83, -58]), at([-90, -63.5]), at(hook)],
        body: ([[-2.6, 2.2], [2.6, 2.2], [2.1, 9], [-2.1, 9]] as Pt[]).map(p => at(hang(p[0], p[1]))),
        cap: ([[-3.2, 2.2], [0, -0.2], [3.2, 2.2]] as Pt[]).map(p => at(hang(p[0], p[1]))),
        glow: at(hang(0, 5.6)),
        drawn: st.ink >= 1 ? 1 : clamp01((st.ink - 0.9) / 0.1),
        lit,
        flare: lit > 0 && lit < 1 ? Math.sin(Math.PI * lit) : 0,
        flicker: 0.82 + 0.1 * Math.sin(t / 83) + 0.06 * Math.sin(t / 37 + 1.3) + 0.04 * Math.sin(t / 151),
    }
}

// ---- the world around it ----------------------------------------------------------------------

export const HORIZON = 312
export const MOON = { x: 128, y: 74, r: 20 }

/** Visible bounds in screen coordinates; wider and taller than 800 x 460 on an expanded phone. */
export interface Bounds {
    left: number
    right: number
    top: number
    bottom: number
}

/**
 * The scene is authored against `right` (the land side before mirroring). Mirroring about the
 * centre maps the bounds onto themselves, since the stage extends equally either way.
 */
export const landProfile = (right: number): Pt[] => {
    const pts = flat("M 620 351 C 652 347 680 333 700 314 C 720 294 756 272 812 262", 14).pts
    const far = right + 12
    if (far > 812) {
        pts.push([far, 262 - (far - 812) * 0.06])
    }
    return pts
}

export const shoreY = (x: number): number => 352 + (x - 614) * 0.03

export const profileYAt = (profile: Pt[], x: number): number => {
    for (let i = 1; i < profile.length; i++) {
        const a = profile[i - 1], b = profile[i]
        if (x <= b[0]) {
            return lerp(a[1], b[1], (x - a[0]) / (b[0] - a[0] || 1))
        }
    }
    return profile[profile.length - 1][1]
}

export interface Land {
    profile: Pt[]
    poly: Pt[]
    shore: Pt[]
    wetSand: Pt[]
    hatches: { pts: Pt[]; d: number }[]
    pines: { pts: Pt[]; i: number }[]
}

const pinePoints = (x: number, b: number, h: number): Pt[] => {
    const tiers = 4
    const w = h * 0.34
    const right: Pt[] = []
    for (let j = 1; j <= tiers; j++) {
        const y = b - h + (h * 0.8 * j) / tiers
        const ww = w * (0.35 + (0.65 * j) / tiers)
        right.push([x + ww, y])
        if (j < tiers) {
            right.push([x + ww * 0.4, y - h * 0.05])
        }
    }
    right.push([x + 1.3, b - h * 0.2])
    right.push([x + 1.3, b])
    const left = right.map(p => [2 * x - p[0], p[1]] as Pt).reverse()
    return [[x, b - h], ...right, ...left]
}

export const buildLand = (right: number): Land => {
    const profile = landProfile(right)
    const far = profile[profile.length - 1][0]
    const poly: Pt[] = profile.concat([
        [far, shoreY(far) + 6],
        [620, shoreY(620) + 1],
    ])
    const shore = flat(`M 614 352 Q 700 355 ${far} ${shoreY(far)}`).pts
    const wetSand = flat(`M 646 356 Q 720 359 ${far} ${shoreY(far) + 3}`).pts

    const random = rng(3)
    const hatches: Land["hatches"] = []
    for (let x = 688; x < far - 2; x += 5.2) {
        const top = profileYAt(profile, x) + 2.5
        const end = Math.min(top + 7 + random() * 12 * clamp01((x - 680) / 40), shoreY(x) - 6)
        if (end - top >= 3) {
            hatches.push({ pts: [[x, top], [x - (end - top) * 0.3, end]], d: random() })
        }
    }

    const sizes: [number, number][] = [[724, 30], [741, 42], [757, 34], [775, 48], [794, 38]]
    const more = rng(9)
    for (let x = 812; x < far - 6; x += 15 + more() * 6) {
        sizes.push([x, 30 + more() * 18])
    }
    const pines = sizes.map(([x, h], i) => ({ pts: pinePoints(x, profileYAt(profile, x) + 1, h), i }))
    return { profile, poly, shore, wetSand, hatches, pines }
}

/** The rock the bow rides up, a smaller one in front of the hull, and two off the far shore. */
export const ROCK_BOW = flat("M 128 399 L 133 391 L 139 387 L 144 381 L 150 377 L 156 378 L 161 373 L 167 375 L 172 381 L 176 388 L 181 399").pts
export const ROCK_NEAR = flat("M 104 399 L 108 394 L 114 392 L 119 393 L 124 397 L 127 399").pts
export const ROCK_SHORE_A = flat("M 640 366 L 645 360 L 652 358 L 657 362 L 661 366").pts
export const ROCK_SHORE_B = flat("M 670 371 L 675 366 L 681 365 L 686 371").pts
export const ROCK_BOW_HATCH = ["M 146 385 L 149 392", "M 153 382 L 156 390", "M 160 379 L 163 387", "M 166 380 L 169 388", "M 139 392 L 141 397", "M 112 395 L 114 398"].map(
    d => flat(d).pts,
)
export const ROCK_SHORE_HATCH = flat("M 649 361 L 651 365").pts

export const circlePoints = (cx: number, cy: number, r: number, a0: number = 0, a1: number = Math.PI * 2, n: number = 64): Pt[] => {
    const out: Pt[] = []
    for (let i = 0; i <= n; i++) {
        const a = a0 + ((a1 - a0) * i) / n
        out.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r])
    }
    return out
}

export const MOON_RIM = circlePoints(MOON.x, MOON.y, MOON.r, -Math.PI / 2, Math.PI * 1.5)
export const MOON_TERMINATOR: Pt[] = Array.from({ length: 25 }, (_, i) => {
    const a = -Math.PI / 2 + (Math.PI * i) / 24
    return [MOON.x - MOON.r * 0.38 * Math.cos(a), MOON.y + MOON.r * Math.sin(a)] as Pt
})
export const MOON_HATCH: Pt[][] = [-12, -7, -2, 3, 8, 13].map(dy => {
    const half = Math.sqrt(MOON.r * MOON.r - dy * dy)
    return [
        [MOON.x - half + 2, MOON.y + dy],
        [MOON.x - half * 0.38 - 1.5, MOON.y + dy],
    ]
})

/** A thin streak of cloud below the moon, centred on (cx, cy). Never across the moon itself: a lens
 *  through the disc reads as a ringed planet. */
export const cloudPoints = (cx: number, cy: number): { lens: Pt[]; under: Pt[] } => {
    const top = flat(`M ${cx - 68} ${cy} Q ${cx - 8} ${cy - 9} ${cx + 68} ${cy}`, 16).pts
    const bottom = flat(`M ${cx + 68} ${cy} Q ${cx + 10} ${cy + 5} ${cx - 68} ${cy}`, 16).pts
    return {
        lens: top.concat(bottom.slice(1)),
        under: flat(`M ${cx - 38} ${cy + 7} Q ${cx + 4} ${cy + 10} ${cx + 46} ${cy + 6.5}`, 12).pts,
    }
}
export const CLOUD = { x: 96, y: 124, drift: 10, period: 8000 }

export interface Star {
    x: number
    y: number
    r: number
    ph: number
    per: number
    cross: boolean
}

/** Stars in the open sky: never behind the wordmark, the card, the moon, the land or the ship. */
export const buildStars = (b: Bounds): Star[] => {
    const random = rng(11)
    const out: Star[] = []
    const want = Math.round(22 * ((b.right - b.left) / 800))
    for (let tries = 0; out.length < want && tries < 2000; tries++) {
        const x = b.left + random() * (b.right - b.left)
        const y = Math.max(b.top, 0) + 8 + random() * (280 - Math.max(b.top, 0))
        if (x > 180 && x < 620 && y < 162) continue
        if (x > 158 && x < 642 && y >= 150) continue
        if (Math.hypot(x - MOON.x, y - MOON.y) < 46) continue
        if (x > 600 && y > 228) continue
        if (x < 175 && y > 190) continue
        out.push({ x, y, r: 0.5 + random() * 0.7, ph: random() * 6.28, per: 1400 + random() * 2600, cross: random() < 0.18 })
    }
    return out
}

export interface Dash {
    x: number
    y: number
    len: number
    a: number
    ph: number
    per: number
    amp: number
    d: number
}

/** Short strokes of light on the water, longer and brighter toward the viewer. */
export const buildWater = (b: Bounds): Dash[] => {
    const random = rng(5)
    const out: Dash[] = []
    const span = b.right - b.left + 20
    for (let k = 0; ; k++) {
        const y = 316 + 3.1 * k + 0.2 * k * k
        if (y > b.bottom - 2) break
        const n = Math.round((6 + Math.floor(k * 0.6)) * (span / 820))
        for (let j = 0; j < n; j++) {
            const x = b.left - 10 + random() * span
            const len = 4 + k * 1.4 + random() * (6 + k * 0.6)
            // Never behind the land.
            if (x + len > 612 && y < shoreY(x + len) + 8) continue
            out.push({ x, y, len, a: Math.min(0.5, 0.14 + k * 0.018), ph: random() * 6.28, per: 2200 + random() * 3000, amp: 2 + random() * 5, d: random() })
        }
    }
    return out
}

/** One row of the moon's path on the water. */
export interface GlitterRow {
    y: number
    hw: number
    row: number
}

export const buildGlitter = (b: Bounds): GlitterRow[] => {
    const out: GlitterRow[] = []
    let y = HORIZON + 5
    let row = 0
    while (y < Math.min(452, b.bottom - 4)) {
        out.push({ y, hw: 3 + (y - HORIZON) * 0.1, row })
        y += 4 + (y - HORIZON) * 0.045
        row++
    }
    return out
}

export interface Drop {
    from: Pt
    to: Pt
    alpha: number
    width: number
}

/**
 * A burst of spray from `at`, `dt` ms after it starts: short streaks along each droplet's path,
 * thrown up in a fan, carried downwind (+x), falling back. Deterministic from `seed`.
 */
export const burst = (at: Pt, dt: number, seed: number, n: number, speed: [number, number], fan: number, life: [number, number], wind: number = 25): Drop[] => {
    if (dt < 0) return []
    const random = rng(seed)
    const out: Drop[] = []
    for (let i = 0; i < n; i++) {
        const ang = ((-90 + (random() - 0.5) * fan) * Math.PI) / 180
        const sp = speed[0] + random() * (speed[1] - speed[0])
        const lifeMs = life[0] + random() * (life[1] - life[0])
        const delay = random() * 90
        const width = 0.7 + random() * 0.6
        const tt = (dt - delay) / 1000
        if (tt < 0 || tt * 1000 > lifeMs) continue
        const vx = Math.cos(ang) * sp + wind
        const vy = Math.sin(ang) * sp
        const g = 420
        const x = at[0] + vx * tt
        const y = at[1] - 2 + vy * tt + 0.5 * g * tt * tt
        if (y > at[1] + 6) continue
        const cvy = vy + g * tt
        const m = Math.hypot(vx, cvy) || 1
        const l = 2 + m * 0.022
        out.push({ from: [x - (vx / m) * l, y - (cvy / m) * l], to: [x, y], alpha: 0.85 * (1 - (tt * 1000) / lifeMs), width })
    }
    return out
}

/**
 * All the spray at `t`: the bow throwing it up each time she plunges into a sea on the way in, the
 * great burst when she strikes, and the breakers bursting over the rock after.
 */
export const sprayAt = (t: number): Drop[] => {
    const out: Drop[] = []
    // Plunges: the bow is lowest a quarter turn into each swell period.
    if (t >= SCENE.sail[0] && t < SCENE.impact) {
        const turn = 2 * Math.PI * SWELL_PERIOD
        const n = Math.floor((t - (Math.PI / 2) * SWELL_PERIOD) / turn)
        const at = (Math.PI / 2) * SWELL_PERIOD + n * turn
        const st = shipStateAt(at)
        const bow = shipPoint(st, 82, -6)
        const near = st.s / SHIP_AGROUND.s
        out.push(...burst(bow, t - at, 700 + n, Math.round(16 * near), [30 * near, 110 * near], 110, [450, 800], 40))
    }
    const dt = t - SCENE.impact
    if (dt >= 0 && dt < 2200) {
        out.push(...burst(CONTACT, dt, 7, 64, [60, 250], 140, [700, 1500]))
    }
    const hit = lastBreaker(t)
    if (t - hit < 1500) {
        const n = Math.round((hit - BREAKERS_FROM) / BREAKER_MS)
        out.push(...burst([CONTACT[0] + 6, CONTACT[1] + 2], t - hit, 400 + n, 34, [50, 180], 120, [600, 1200], 35))
    }
    return out
}

/** A ball of white water in a plume, in screen coordinates. */
export interface Puff {
    x: number
    y: number
    r: number
    /** 0..1: 1 fresh and white, fading as it falls and breaks up. */
    life: number
}

/** The white water thrown up when a sea bursts on the rock: balls of foam rising, spreading, falling. */
const plume = (at: Pt, dt: number, seed: number, n: number, height: number, durMs: number): Puff[] => {
    if (dt < 0 || dt > durMs) return []
    const random = rng(seed)
    const out: Puff[] = []
    for (let i = 0; i < n; i++) {
        const u = random()
        const ang = ((-90 + (random() - 0.5) * 100) * Math.PI) / 180
        const sp = height * (0.55 + 0.45 * u) * 2.9
        const delay = random() * durMs * 0.18
        const tt = (dt - delay) / 1000
        if (tt < 0) continue
        const life = 1 - (dt - delay) / (durMs - delay)
        if (life <= 0) continue
        const g = 520
        const vx = Math.cos(ang) * sp * 0.6 + 30
        const vy = Math.sin(ang) * sp
        const y = at[1] + vy * tt + 0.5 * g * tt * tt
        if (y > at[1] + 4) continue
        out.push({ x: at[0] + vx * tt, y, r: (2.5 + random() * 4) * (0.5 + 0.8 * Math.min(1, tt * 6)) * (0.4 + 0.6 * life), life })
    }
    return out
}

/** The plume at `t`: the great one as she strikes, then one with every breaker. */
export const plumeAt = (t: number): Puff[] => {
    const dt = t - SCENE.impact
    const out = plume([CONTACT[0] - 2, CONTACT[1]], dt, 31, 34, 62, 1500)
    const hit = lastBreaker(t)
    if (t - hit < 1100) {
        const n = Math.round((hit - BREAKERS_FROM) / BREAKER_MS)
        out.push(...plume([CONTACT[0] + 4, CONTACT[1] + 2], t - hit, 60 + n, 16, 30 + 14 * rng(80 + n)(), 1100))
    }
    return out
}

/** The screen's nudge when the ship strikes, in logical pixels. */
export const shakeAt = (t: number): Pt => {
    const dt = t - SCENE.impact
    if (dt <= 0 || dt >= 600) {
        return [0, 0]
    }
    const e = Math.exp(-dt / 130)
    return [3.4 * e * Math.sin(dt / 24), 2.4 * e * Math.sin(dt / 29 + 1)]
}
